import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, Trash2, User, X, ZoomIn } from "lucide-react";

/**
 * PhotoAvatar - reusable profile photo widget.
 * Clicking the avatar opens a full-size lightbox view (when a photo is set).
 */
export default function PhotoAvatar({ photoUrl, name = "", uploadUrl, deleteUrl, authHeader, onChange, size = 96, editable = true }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const [zoomed, setZoomed] = useState(false);

  const initials = (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0])
    .join("")
    .toUpperCase() || "?";

  useEffect(() => {
    if (!zoomed) return;
    const onKey = (e) => { if (e.key === "Escape") setZoomed(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomed]);

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Yalnızca görsel dosya yükleyebilirsiniz"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Görsel 5 MB üzeri olamaz"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(uploadUrl, fd, { headers: authHeader || {}, withCredentials: true });
      onChange && onChange(r.data.photo_url);
      toast.success("Fotoğraf güncellendi");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Yükleme başarısız");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!deleteUrl) return;
    setBusy(true);
    try {
      await axios.delete(deleteUrl, { headers: authHeader || {}, withCredentials: true });
      onChange && onChange("");
      toast.success("Fotoğraf kaldırıldı");
    } catch (err) {
      toast.error("Silinemedi");
    } finally { setBusy(false); }
  };

  const canZoom = !!photoUrl;

  return (
    <div className="flex items-center gap-4">
      <button
        type="button"
        onClick={() => canZoom && setZoomed(true)}
        style={{ width: size, height: size }}
        className={`relative rounded-full overflow-hidden bg-slate-100 border border-slate-200 shrink-0 shadow-sm group ${canZoom ? "cursor-zoom-in" : "cursor-default"}`}
        data-testid="photo-avatar"
        aria-label={canZoom ? "Fotoğrafı büyüt" : "Profil fotoğrafı"}
        disabled={!canZoom}
      >
        {photoUrl ? (
          <>
            <img src={photoUrl} alt={name} className="w-full h-full object-cover" />
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-slate-900/0 group-hover:bg-slate-900/40 transition-colors">
              <ZoomIn size={Math.round(size * 0.28)} className="text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
            </span>
          </>
        ) : (
          <span className="w-full h-full flex items-center justify-center text-slate-500 font-bold" style={{ fontSize: size * 0.32 }}>
            {initials !== "?" ? initials : <User size={size * 0.4} />}
          </span>
        )}
        {busy && (
          <span className="absolute inset-0 bg-white/60 flex items-center justify-center text-xs text-slate-700">…</span>
        )}
      </button>
      {editable && (
        <div className="flex flex-col gap-1.5">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="photo-file-input"
            onChange={e => { upload(e.target.files?.[0]); e.target.value = ""; }}
          />
          <button
            type="button"
            data-testid="photo-upload-btn"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            <Camera size={13} /> {photoUrl ? "Değiştir" : "Fotoğraf Yükle"}
          </button>
          {photoUrl && deleteUrl && (
            <button
              type="button"
              data-testid="photo-delete-btn"
              disabled={busy}
              onClick={remove}
              className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-500 hover:text-red-600 hover:border-red-200 disabled:opacity-60"
            >
              <Trash2 size={12} /> Kaldır
            </button>
          )}
        </div>
      )}

      {zoomed && photoUrl && (
        <div
          data-testid="photo-lightbox"
          onClick={() => setZoomed(false)}
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-10 cursor-zoom-out"
          style={{ background: "radial-gradient(ellipse at center, rgba(15,23,42,0.85) 0%, rgba(2,6,23,0.95) 100%)" }}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setZoomed(false); }}
            data-testid="photo-lightbox-close"
            className="absolute top-5 right-5 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center border border-white/20"
            aria-label="Kapat"
          >
            <X size={18} />
          </button>
          <figure
            onClick={(e) => e.stopPropagation()}
            className="flex flex-col items-center gap-4 max-h-full max-w-full"
          >
            <img
              src={photoUrl}
              alt={name}
              className="max-h-[82vh] max-w-full rounded-lg shadow-2xl object-contain bg-white photo-zoom-in"
            />
            {name && (
              <figcaption className="text-white/90 text-sm sm:text-base font-semibold tracking-tight text-center max-w-[80vw]">
                {name}
              </figcaption>
            )}
          </figure>
        </div>
      )}
    </div>
  );
}
